import React, {useEffect, useRef, useState, memo} from 'react';
import ReactDOM from 'react-dom';
import Flickity from 'flickity-fade';
import PropTypes from 'prop-types';
import cx from 'classnames';

import 'flickity-imagesloaded';

/* eslint-disable react/display-name */
const Slider = memo(({children, slidesId, onSlideChange, onSliderReady, flickityConfig, classPrefix}) => {
  const sliderContainer = useRef(null);
  const [slider, setSlider] = useState(null);
  const prevSlidesId = useRef(null);
  const sliderRef = useRef(slider);

  // Setup on unmount callback to destroy flickity slider
  useEffect(
    () => () => {
      if (!sliderRef.current) return;
      sliderRef.current.destroy();
      if (onSliderReady) {
        onSliderReady(false);
      }
    },
    []
  );

  // Set sliderRef so we have flickity instance for unmount of component
  useEffect(() => {
    if (slider && !sliderRef.current) {
      sliderRef.current = slider;
      sliderRef.current.reloadCells();
      sliderRef.current.resize();
      sliderRef.current.on('dragStart', () => {
        window.console.log('dragStart');
        sliderContainer.current.classList.add(`${classPrefix}__slider--dragging`);
      });
      sliderRef.current.on('dragEnd', () =>
        sliderContainer.current.classList.remove(`${classPrefix}__slider--dragging`)
      );
      if (onSlideChange) {
        sliderRef.current.on('change', (index) => {
          window.console.log('change');
          onSlideChange(index);
        });
      }
    } else if (!slider && sliderRef.current) {
      sliderRef.current.destroy();
      sliderRef.current = null;
      if (onSliderReady) {
        onSliderReady(false);
      }
    }
  }, [slider]);

  useEffect(() => {
    if (slidesId === prevSlidesId.current) {
      return;
    }

    prevSlidesId.current = slidesId;

    if (!sliderRef.current && children.length > 1) {
      setSlider(new Flickity(sliderContainer.current, flickityConfig));
    } else if (sliderRef.current && children.length > 1) {
      sliderRef.current.reloadCells();
      sliderRef.current.resize();
    } else if (sliderRef.current && children.length <= 1) {
      setSlider(null);
    }
  }, [children]);

  const sliderPortal = () => {
    if (!sliderContainer.current) {
      return null;
    }

    const flickitySlider = sliderContainer.current.querySelector('.flickity-slider');

    if (flickitySlider) {
      return ReactDOM.createPortal(children, flickitySlider);
    } else {
      return children;
    }
  };

  return (
    <div
      className={cx([
        children.length > 1 && `${classPrefix}__slider`,
        children.length <= 1 && `${classPrefix}__no-slider`,
      ])}
      ref={sliderContainer}
    >
      {sliderPortal()}
    </div>
  );
});
/* eslint-enable react/display-name */

Slider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  slidesId: PropTypes.string.isRequired,
  onSlideChange: PropTypes.func,
  onSliderReady: PropTypes.func,
  flickityConfig: PropTypes.object.isRequired,
  classPrefix: PropTypes.string.isRequired,
};

export default Slider;
